import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { watchOnce } from "@vueuse/core";
import _debounce from "./debounce.min.js";
import TapDetector from "./TapDetector";

import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  watch,
} from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomZoomer',
  props: {
  minScale: { type: Number, default: 1 },
  maxScale: { type: Number, default: 5 },
  zoomed: { type: Boolean, default: false },
  resetTrigger: { type: Number, default: 1e5 },
  aspectRatio: { type: Number, default: 1 },
  backgroundColor: { type: String, default: "transparent" },
  pivot: { type: String, default: "cursor" }, // other options: image-center
  zoomingElastic: { type: Boolean, default: true },
  limitTranslation: { type: Boolean, default: true },
  doubleClickToZoom: { type: Boolean, default: true },
  mouseWheelToZoom: { type: Boolean, default: true },
  rotation: { type: Number, default: 0 },
},
  emits: ["update:zoomed", "swipe"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const containerWidth = ref<number>(1);
const containerHeight = ref<number>(1);
const containerLeft = ref<number>(0);
const containerTop = ref<number>(0);

const translateX = ref<number>(0);
const animTranslateX = ref<number>(0);
const translateY = ref<number>(0);
const animTranslateY = ref<number>(0);
const scale = ref<number>(1);
const animScale = ref<number>(1);

// Mouse states
const lastFullWheelTime = ref<number>(0);
const lastWheelTime = ref(0);
const lastWheelDirection = ref<"y" | "x">("y");
const isPointerDown = ref<boolean>(false);
const pointerPosX = ref<number>(-1);
const pointerPosY = ref<number>(-1);
const twoFingerInitDist = ref<number>(0);
const panLocked = ref<boolean>(true);
// Others
const raf = ref<number | null>(null);
const tapDetector = ref<TapDetector | null>(null);
let resizeObserver: ResizeObserver | null = null;
const onWindowResizeDebounced = _debounce(
  () => requestAnimationFrame(onWindowResize),
  150,
); 

const rootRef = shallowRef<HTMLDivElement | null>(null);

const wrapperStyle = computed(() => {
  // console.log("containerWidth.value: ", containerWidth.value); //is set to 100px incorrectly
  // console.log("animTranslateX.value: ", animTranslateX.value);
  const translateX = containerWidth.value * animTranslateX.value;
  const translateY = containerHeight.value * animTranslateY.value;
  return {
    transform: [
      `translate(${translateX}px, ${translateY}px)`,
      `scale(${animScale.value})`,
      `rotate(${props.rotation}deg)`,
    ].join(" "),
  };
});

watch(scale, (val) => {
  if (val !== 1) {
    emit("update:zoomed", true);
    panLocked.value = false;
  }
});

onBeforeMount(() => {
  watchOnce(rootRef,() => {
    setTimeout(onWindowResize, 250);
    setTimeout(onWindowResize, 500);
  })
})

onMounted(() => {
  tapDetector.value = new TapDetector();
  tapDetector.value.attach(rootRef.value);
  if (props.doubleClickToZoom) {
    tapDetector.value.onDoubleTap(onDoubleTap);
  }

  window.addEventListener("resize", onWindowResizeDebounced);

  if (typeof ResizeObserver !== "undefined" && rootRef.value) {
    resizeObserver = new ResizeObserver(onWindowResizeDebounced);
    resizeObserver.observe(rootRef.value);
  }

  onWindowResize();
  refreshContainerPos();
  loop();
});

onBeforeUnmount(() => {
  tapDetector.value?.detach(rootRef.value);
  window.removeEventListener("resize", onWindowResizeDebounced);
  resizeObserver?.disconnect();
  resizeObserver = null;
  if (typeof raf.value === "number") {
    window.cancelAnimationFrame(raf.value);
  }
});

function reset() {
  scale.value = 1;
  panLocked.value = true;
  translateX.value = 0;
  translateY.value = 0;
}

function zoomIn(scale = 2) {
  tryToScale(scale);
  onInteractionEnd();
}
function zoomOut(scale = 0.5) {
  tryToScale(scale);
  onInteractionEnd();
}


function tryToScale(scaleDelta: number) {
  let newScale = scale.value * scaleDelta;
  if (props.zoomingElastic) {
    // damping
    if (newScale < props.minScale || newScale > props.maxScale) {
      let log = Math.log2(scaleDelta);
      log *= 0.2;
      scaleDelta = Math.pow(2, log);
      newScale = scale.value * scaleDelta;
    }
  } else {
    if (newScale < props.minScale) newScale = props.minScale;
    else if (newScale > props.maxScale) newScale = props.maxScale;
  }
  scaleDelta = newScale / scale.value;
  scale.value = newScale;
  if (props.pivot !== "image-center") {
    const normMousePosX =
      (pointerPosX.value - containerLeft.value) / containerWidth.value;

    const normMousePosY =
      (pointerPosY.value - containerTop.value) / containerHeight.value;
    translateX.value =
      (0.5 + translateX.value - normMousePosX) * scaleDelta +
      normMousePosX -
      0.5;
    translateY.value =
      (0.5 + translateY.value - normMousePosY) * scaleDelta +
      normMousePosY -
      0.5;
  }
}

function setPointerPosCenter() {
  pointerPosX.value = containerLeft.value + containerWidth.value / 2;
  pointerPosY.value = containerTop.value + containerHeight.value / 2;
}

function onPointerMove(newMousePosX: number, newMousePosY: number) {
  if (isPointerDown.value) {
    const pixelDeltaX = newMousePosX - pointerPosX.value;
    const pixelDeltaY = newMousePosY - pointerPosY.value;
    // console.log('pixelDeltaX, pixelDeltaY', pixelDeltaX, pixelDeltaY)
    if (!panLocked.value) {
      translateX.value += pixelDeltaX / containerWidth.value;
      translateY.value += pixelDeltaY / containerHeight.value;
    }
  }
  pointerPosX.value = newMousePosX;
  pointerPosY.value = newMousePosY;
}

const onInteractionEnd = _debounce(function () {
  limit();
  panLocked.value = scale.value === 1;
  emit("update:zoomed", !panLocked.value);
}, 100);

function limit() {
  // scale
  if (scale.value < props.minScale) {
    scale.value = props.minScale;
    // FIXME this sometimes will not reset when pinching in
    // tryToScale(minScale / scale)
  } else if (scale.value > props.maxScale) {
    tryToScale(props.maxScale / scale.value);
  }
  // translate
  if (props.limitTranslation) {
    const translateLimit = calcTranslateLimit();
    if (Math.abs(translateX.value) > translateLimit.x) {
      translateX.value *= translateLimit.x / Math.abs(translateX.value);
    }
    if (Math.abs(translateY.value) > translateLimit.y) {
      translateY.value *= translateLimit.y / Math.abs(translateY.value);
    }
  }
}

function calcTranslateLimit() {
  if (getMarginDirection() === "y") {
    const imageToContainerRatio =
      containerWidth.value / props.aspectRatio / containerHeight.value;
    let translateLimitY = (scale.value * imageToContainerRatio - 1) / 2;
    if (translateLimitY < 0) translateLimitY = 0;
    return {
      x: (scale.value - 1) / 2,
      y: translateLimitY,
    };
  } else {
    const imageToContainerRatio =
      (containerHeight.value * props.aspectRatio) / containerWidth.value;
    let translateLimitX = (scale.value * imageToContainerRatio - 1) / 2;
    if (translateLimitX < 0) translateLimitX = 0;
    return {
      x: translateLimitX,
      y: (scale.value - 1) / 2,
    };
  }
}

function getMarginDirection() {
  const containerRatio = containerWidth.value / containerHeight.value;
  return containerRatio > props.aspectRatio ? "x" : "y";
}

function onDoubleTap(ev: any) {
  if (scale.value === 1) {
    if (ev.clientX > 0) {
      pointerPosX.value = ev.clientX;
      pointerPosY.value = ev.clientY;
    }
    tryToScale(Math.min(3, props.maxScale));
  } else {
    reset();
  }
  onInteractionEnd();
}


function onWindowResize() {
  if (rootRef.value !== null) {
    const styles = window.getComputedStyle(rootRef.value);

    const nextWidth =
      parseFloat(styles.width) || rootRef.value.clientWidth || containerWidth.value;
    const nextHeight =
      parseFloat(styles.height) || rootRef.value.clientHeight || containerHeight.value;

    const sizeChanged =
      Math.abs(nextWidth - containerWidth.value) > 1 ||
      Math.abs(nextHeight - containerHeight.value) > 1;

    containerWidth.value = nextWidth;
    containerHeight.value = nextHeight;
    setPointerPosCenter();

    if (sizeChanged) reset();
    return;
  }

  setPointerPosCenter();
  reset();
}

function refreshContainerPos() {
  if (rootRef.value !== null) {
    const rect = rootRef.value?.getBoundingClientRect();
    containerLeft.value = rect.left;
    containerTop.value = rect.top;
  }
}

function loop() {
  animScale.value = gainOn(animScale.value, scale.value);
  animTranslateX.value = gainOn(animTranslateX.value, translateX.value);
  animTranslateY.value = gainOn(animTranslateY.value, translateY.value);
  raf.value = window.requestAnimationFrame(loop);

  // console.log(`animTranslateX: ${animTranslateX.value}, animTranslateY: ${animTranslateY.value}`)
  // console.log(
  //   `translateX: ${translateX.value}, translateY: ${translateY.value}`
  // );

  // console.log('loop', raf)
}

function gainOn(from: number, to: number) {
  const delta = (to - from) * 0.3;
  // console.log('gainOn', from, to, from + delta)
  if (Math.abs(delta) > 1e-5) {
    return from + delta;
  } else {
    return to;
  }
}


let lastStandardWheelAt = 0;

function onWheelEvent(ev: WheelEvent) {
  if (!props.mouseWheelToZoom) return;

  lastStandardWheelAt = Date.now();

  ev.preventDefault();


  let deltaX = ev.deltaX;
  let deltaY = ev.deltaY;
  if (ev.deltaMode === 1) { deltaX *= 16; deltaY *= 16; }
  else if (ev.deltaMode === 2) { deltaX *= 100; deltaY *= 100; }

  const currTime = Date.now();


  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    if (currTime - lastWheelTime.value > 50) {
      emit("swipe", deltaX > 0 ? "left" : "right");
      lastWheelTime.value = currTime;
    }
    return;
  }


  if (currTime - lastWheelTime.value < 16) return;
  lastWheelTime.value = currTime;

 
  refreshContainerPos();
  pointerPosX.value = ev.clientX;
  pointerPosY.value = ev.clientY;

 
  const wheelDelta = Math.max(-240, Math.min(240, -deltaY));
  onMouseWheelDo(wheelDelta);
}

function onMouseWheel(ev: any) {
  if (!props.mouseWheelToZoom) return;

  if (Date.now() - lastStandardWheelAt < 200) return;


  ev.preventDefault();
  if (ev.detail) ev.wheelDelta = ev.detail * -10;
  const currTime = Date.now();
  if (Math.abs(ev.wheelDelta) === 120) {

    if (currTime - lastFullWheelTime.value > 50) {
      onMouseWheelDo(ev.wheelDelta);
      lastFullWheelTime.value = currTime;
    }
  } else {
    if (currTime - lastWheelTime.value > 50 && typeof ev.deltaX === "number") {
      lastWheelDirection.value =
        ev.detail == 0 && Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? "x" : "y";
      if (lastWheelDirection.value === "x") {
        emit("swipe", ev.deltaX > 0 ? "left" : "right");
      }
    }
    if (lastWheelDirection.value === "y") {
      onMouseWheelDo(ev.wheelDelta);
    }
  }
  lastWheelTime.value = currTime;
}

function onMouseWheelDo(wheelDelta: number) {
  const scaleDelta = Math.pow(1.25, wheelDelta / 120);
  tryToScale(scaleDelta);
  onInteractionEnd();
}

function onMouseDown(ev: MouseEvent) {
  refreshContainerPos();
  isPointerDown.value = true;
 
  pointerPosX.value = ev.clientX;
  pointerPosY.value = ev.clientY;
  // console.log('onMouseDown', ev)
}

function onMouseUp(ev: MouseEvent) {
  isPointerDown.value = false;
  onInteractionEnd();
}

function onMouseMove(ev: MouseEvent) {
  onPointerMove(ev.clientX, ev.clientY);

}

function onTouchStart(ev: TouchEvent) {
  if (ev.touches.length === 1) {
    refreshContainerPos();
    pointerPosX.value = ev.touches[0].clientX;
    pointerPosY.value = ev.touches[0].clientY;
    isPointerDown.value = true;
  } else if (ev.touches.length === 2) {
    isPointerDown.value = true;
    // pos
    pointerPosX.value = (ev.touches[0].clientX + ev.touches[1].clientX) / 2;
    pointerPosY.value = (ev.touches[0].clientY + ev.touches[1].clientY) / 2;
    // dist
    const distX = ev.touches[0].clientX - ev.touches[1].clientX;
    const distY = ev.touches[0].clientY - ev.touches[1].clientY;
    twoFingerInitDist.value = Math.sqrt(distX * distX + distY * distY);
  }
  // console.log('onTouchStart', ev.touches)
}

function onTouchEnd(ev: TouchEvent) {
  if (ev.touches.length === 0) {
    isPointerDown.value = false;
    // Near 1 to set 1
    if (Math.abs(scale.value - 1) < 0.1) scale.value = 1;
    onInteractionEnd();
  } else if (ev.touches.length === 1) {
    pointerPosX.value = ev.touches[0].clientX;
    pointerPosY.value = ev.touches[0].clientY;
  }
}

function onTouchMove(ev: TouchEvent) {
  if (ev.touches.length === 1) {
    onPointerMove(ev.touches[0].clientX, ev.touches[0].clientY);
  } else if (ev.touches.length === 2) {
    // pos
    const newMousePosX = (ev.touches[0].clientX + ev.touches[1].clientX) / 2;
    const newMousePosY = (ev.touches[0].clientY + ev.touches[1].clientY) / 2;
    onPointerMove(newMousePosX, newMousePosY);
    pointerPosX.value = newMousePosX;
    pointerPosY.value = newMousePosY;
    // dist
    const distX = ev.touches[0].clientX - ev.touches[1].clientX;
    const distY = ev.touches[0].clientY - ev.touches[1].clientY;
    const newTwoFingerDist = Math.sqrt(distX * distX + distY * distY);
    tryToScale(newTwoFingerDist / twoFingerInitDist.value);
    twoFingerInitDist.value = newTwoFingerDist;
  }
  // console.log('onTouchMove', pointerPosX, pointerPosY)
}

watch(
  computed(() => props.zoomed),
  (newV, oldV) => {
    if (oldV === true && newV === false) {
      reset()
    }
  }
);


__expose({
  reset,
  refresh: refreshContainerPos
})


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "rootRef",
    ref: rootRef,
    class: "vue-zoomer",
    style: _normalizeStyle({ backgroundColor: __props.backgroundColor }),
    onWheel: _withModifiers(onWheelEvent, ["stop"]),
    onMousewheel: _withModifiers(onMouseWheel, ["stop"]),
    "on:DOMMouseScroll": _withModifiers(onMouseWheel, ["stop"]),
    onMousedown: _withModifiers(onMouseDown, ["stop"]),
    onMouseup: _withModifiers(onMouseUp, ["stop"]),
    onMousemove: _withModifiers(onMouseMove, ["stop"]),
    onTouchstart: _withModifiers(onTouchStart, ["stop"]),
    onTouchend: _withModifiers(onTouchEnd, ["stop"]),
    onTouchmove: _withModifiers(onTouchMove, ["stop"])
  }, [
    _createElementVNode("div", {
      class: "zoomer",
      style: _normalizeStyle(wrapperStyle.value)
    }, [
      _renderSlot(_ctx.$slots, "default")
    ], 4)
  ], 36))
}
}

})